import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { BlockEditor } from '../components/workout/BlockEditor';
import { WorkoutForm } from '../components/workout/WorkoutForm';
import { WorkoutBlock } from '../lib/workout';

type ModalContextType = {
  showWorkoutForm: (props: WorkoutFormProps) => void;
  showBlockEditor: (props: BlockEditorProps) => void;
  closeModal: () => void;
  hasSavedWorkoutDraft: () => boolean;
};

// Define the types for form props
export interface WorkoutFormProps {
  title: string;
  initialData?: {
    description?: string;
    color?: string;
    notes?: string;
    name?: string;
    type_id?: number;
    /**
     * Newly added: list of exercises already attached to the workout being edited. Each exercise
     * contains an id, name and optional link. This enables the WorkoutForm to pre-populate the
     * ExerciseSelector when a coach edits an existing workout.
     */
    exercises?: { id: number; name: string; link: string | null }[];
  };
  onSave: (workout: {
    description: string;
    color: string;
    notes?: string;
    name?: string;
    type_id: number;
    wasEdited: boolean;
    coach_id?: string;
    exercises: { id: number; name: string; link: string | null }[];
  }) => void;
}

export interface BlockEditorProps {
  title: string;
  blockType: string;
  initialData?: Partial<WorkoutBlock>;
  onSave: (blockData: Partial<WorkoutBlock>) => void;
}

// Create the Modal Context
const ModalContext = createContext<ModalContextType | undefined>(undefined);

// Define the types of modals
type ModalType = 'workout' | 'block' | null;

// Keys for localStorage
const MODAL_STATE_KEY = 'rudofit_modal_state';
const WORKOUT_FORM_KEY = 'rudofit_workout_form_data';
const MODAL_CLOSED_FLAG = 'rudofit_modal_manually_closed';

// Helper functions for localStorage
const saveModalState = (modalType: ModalType, props?: Omit<WorkoutFormProps, 'onSave'>) => {
  try {
    if (modalType === 'workout' && props) {
      localStorage.setItem(MODAL_STATE_KEY, JSON.stringify({ type: modalType }));
      localStorage.setItem(WORKOUT_FORM_KEY, JSON.stringify(props));
      // Clear the manually closed flag when saving a new modal state
      localStorage.removeItem(MODAL_CLOSED_FLAG);
    } else if (modalType === null) {
      localStorage.removeItem(MODAL_STATE_KEY);
      localStorage.removeItem(WORKOUT_FORM_KEY);
    }
  } catch (error) {
    console.error('Error saving modal state to localStorage:', error);
  }
};

const loadModalState = (): { modalType: ModalType; workoutFormProps: Omit<WorkoutFormProps, 'onSave'> | null } => {
  try {
    // Check if modal was manually closed in this session
    const wasManuallyClosed = localStorage.getItem(MODAL_CLOSED_FLAG);
    if (wasManuallyClosed) {
      return { modalType: null, workoutFormProps: null };
    }
    
    const modalState = localStorage.getItem(MODAL_STATE_KEY);
    const workoutFormData = localStorage.getItem(WORKOUT_FORM_KEY);
    
    if (modalState && workoutFormData) {
      const parsedState = JSON.parse(modalState);
      const parsedFormData = JSON.parse(workoutFormData);
      
      if (parsedState.type === 'workout') {
        return {
          modalType: 'workout',
          workoutFormProps: parsedFormData
        };
      }
    }
  } catch (error) {
    console.error('Error loading modal state from localStorage:', error);
  }
  
  return { modalType: null, workoutFormProps: null };
};

const clearModalState = () => {
  try {
    localStorage.removeItem(MODAL_STATE_KEY);
    localStorage.removeItem(WORKOUT_FORM_KEY);
    // Set the manually closed flag
    localStorage.setItem(MODAL_CLOSED_FLAG, 'true');
  } catch (error) {
    console.error('Error clearing modal state:', error);
  }
};

const hasSavedWorkoutDraft = (): boolean => {
  try {
    // Don't show draft if it was manually closed
    const wasManuallyClosed = localStorage.getItem(MODAL_CLOSED_FLAG);
    if (wasManuallyClosed) {
      return false;
    }
    return localStorage.getItem(WORKOUT_FORM_KEY) !== null;
  } catch (error) {
    return false;
  }
};

export function ModalProvider({ children }: { children: ReactNode }) {
  // Initialize state from localStorage - restore modal if it was open
  const [modalType, setModalType] = useState<ModalType>(() => {
    const { modalType: savedModalType } = loadModalState();
    return savedModalType;
  });
  
  const [workoutFormProps, setWorkoutFormProps] = useState<WorkoutFormProps | null>(() => {
    const { workoutFormProps: savedProps } = loadModalState();
    return savedProps as WorkoutFormProps | null;
  });
  
  const [blockEditorProps, setBlockEditorProps] = useState<BlockEditorProps | null>(null);
  const [currentOnSave, setCurrentOnSave] = useState<WorkoutFormProps['onSave'] | null>(null);

  const showWorkoutForm = (props: WorkoutFormProps) => {
    // Clear the manually closed flag when opening a new workout form
    try {
      localStorage.removeItem(MODAL_CLOSED_FLAG);
    } catch (error) {
      console.error('Error clearing manually closed flag:', error);
    }
    
    // Check if there's saved form data and merge it with new props
    const savedData = loadModalState();
    
    let finalProps = props;
    if (savedData.workoutFormProps) {
      // Merge saved form data with new props, preserving the onSave callback
      finalProps = {
        ...props,
        title: savedData.workoutFormProps.title || props.title,
        initialData: {
          ...props.initialData,
          ...savedData.workoutFormProps.initialData
        }
      };
    }
    
    setWorkoutFormProps(finalProps);
    setCurrentOnSave(() => props.onSave); // Store the callback separately
    setModalType('workout');
    
    // Save the modal state and form data (without callback) for persistence
    const { onSave, ...serializableProps } = finalProps;
    saveModalState('workout', serializableProps);
  };

  const showBlockEditor = (props: BlockEditorProps) => {
    setBlockEditorProps(props);
    setModalType('block');
    // Block editor is not persisted as it's typically used for quick edits
  };

  const closeModal = () => {
    setModalType(null);
    setWorkoutFormProps(null);
    setBlockEditorProps(null);
    setCurrentOnSave(null);
    // Clear saved modal state when modal is closed
    clearModalState();
  };

  // Clean up localStorage on page unload if no modal is open
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Only clear if no modal is open
      if (modalType === null) {
        clearModalState();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [modalType]);

  // Handle the case where modal is restored but we need a fresh onSave callback
  useEffect(() => {
    if (modalType === 'workout' && workoutFormProps && !currentOnSave) {
      // Modal was restored from localStorage but we don't have an onSave callback
      // We need to wait for the component that triggered this to provide the callback
      // For now, we'll keep the modal open but it won't be functional until re-triggered
      console.log('Modal restored from localStorage, waiting for fresh onSave callback');
    }
  }, [modalType, workoutFormProps, currentOnSave]);

  return (
    <ModalContext.Provider value={{
      showWorkoutForm,
      showBlockEditor,
      closeModal,
      hasSavedWorkoutDraft,
    }}>
      {children}

      {modalType === 'workout' && workoutFormProps && (
        <WorkoutForm
          {...workoutFormProps}
          onSave={currentOnSave || (() => {
            console.warn('No onSave callback available - modal was restored from localStorage');
          })}
          onClose={closeModal}
        />
      )}

      {modalType === 'block' && blockEditorProps && (
        <BlockEditor
          {...blockEditorProps}
          onClose={closeModal}
        />
      )}
    </ModalContext.Provider>
  );
}

export const useModal = () => {
  const context = useContext(ModalContext);
  if (context === undefined) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
};